import { useState } from 'react';

/**
 * Page-level collapsible section — rounded card, light-grey header strip,
 * a "▼ Show"/"▲ Hide" toggle on the right, an optional count badge next to
 * the title, and an optional `extra` slot (header-row controls that don't
 * also toggle collapse, e.g. a "🔄 Refresh" button — stopPropagation'd for
 * you). Extracted from SupplierPortal.js's own local `Section` (2026-08-31)
 * once MyHomePM.js needed the exact same look — before this, MyHomePM.js's
 * sections were each a plain `<h3>` with no surrounding card/header strip
 * at all, and `PastDeliveriesPanel` had grown its own hand-rolled Show/Hide
 * toggle with none of the rounded-card chrome — three subtly different
 * treatments for what's supposed to be one consistent "page section" look
 * across both roles' home pages.
 *
 * Distinct from `CollapsibleSection.js`, which is deliberately smaller/
 * plainer — that one is for a nested sub-list within a section (e.g. a
 * "Closed" bucket at the bottom of this section's own body), not a
 * top-level page section in its own right.
 *
 * Props: `id?` (anchor for scroll-to), `title`, `count?` (badge, omitted
 * when null/undefined), `countColor?` (badge background, default the
 * original blue `#3498db` — pass a neutral grey, e.g. `#95a5a6`, for a
 * count that's purely informational rather than something needing action;
 * `MyHomePM.js`'s Your Tasks/Pending Deliveries badges switch between the
 * two based on whether there's actually anything open, 2026-09-03),
 * `defaultOpen?` (default false), `extra?` (JSX rendered in the header,
 * right-aligned before the Show/Hide toggle), `children`.
 */
export default function Section({ id, title, count, countColor = '#3498db', defaultOpen = false, extra, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderBottom: open ? '1px solid #e0e4e8' : 'none', background: '#f8f9fa' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
          {title}
          {count != null && (
            <span style={{ marginLeft: '8px', background: countColor, color: 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' }}>
              {count}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {extra && <div onClick={e => e.stopPropagation()}>{extra}</div>}
          <span style={{ fontSize: '12px', color: '#aaa' }}>{open ? '▲ Hide' : '▼ Show'}</span>
        </div>
      </div>
      {open && <div style={{ padding: '12px 14px' }}>{children}</div>}
    </div>
  );
}
